import { getBackendURL } from "../components/env";

const formatDays = (days) => {
  // null/undefined means "no value from the server" and must fall through to
  // the generic wording below. Number(null) is 0 (finite), which would
  // otherwise be indistinguishable from a real 0-day window.
  if (days === null || days === undefined) return null;
  const n = Number(days);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// Same sentence the backend returns as `warning`, for places that only have
// the number (the Status badge tooltip).
export const confirmationWindowWarning = (windowDays) => {
  const days = formatDays(windowDays);
  const within = days ? `within ${days} days` : "within the reminder window";
  return `Appointment is ${within}; no automatic confirmation will be scheduled. Consider sending one manually.`;
};

// Fire-and-forget hook used right after a patient row is saved. Never throws:
// scheduling is a courtesy, the save already happened.
export const scheduleConfirmationForPatient = async ({ schema, notesCId, toast }) => {
  if (!notesCId) return null;
  try {
    const response = await fetch(`${getBackendURL()}/twilio/schedule-confirmations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schema: schema || "public", notes_c_id: notesCId }),
    });
    const payload = await response.json();
    if (!response.ok || payload.status !== "success") {
      throw new Error(payload.error || `HTTP ${response.status}`);
    }
    if (payload.warning && toast) {
      toast({
        title: "No automatic confirmation",
        description: payload.warning,
        status: "warning",
        duration: 8000,
        isClosable: true,
      });
    }
    return payload;
  } catch (error) {
    console.warn("[CONFIRMATIONS] Could not schedule confirmations:", error.message);
    return null;
  }
};

export const fetchConfirmationSchedule = async ({ schema, date }) => {
  if (!date) return null;
  try {
    const url =
      `${getBackendURL()}/twilio/confirmation-schedule/` +
      `?schema=${encodeURIComponent(schema || "public")}&date=${encodeURIComponent(date)}`;
    const response = await fetch(url);
    const payload = await response.json();
    if (!response.ok || payload.status !== "success") {
      throw new Error(payload.error || `HTTP ${response.status}`);
    }
    return {
      rows: payload.rows || [],
      autoStatus: payload.auto_status || {},
      windowDays: payload.window_days ?? null,
    };
  } catch (error) {
    console.warn("[CONFIRMATIONS] Could not load the confirmation schedule:", error.message);
    return null;
  }
};

export const cancelConfirmations = async ({ schema, ids }) => {
  const response = await fetch(`${getBackendURL()}/twilio/cancel-confirmations/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schema: schema || "public", ids }),
  });
  const payload = await response.json();
  if (!response.ok || payload.status !== "success") {
    throw new Error(payload.error || `HTTP ${response.status}`);
  }
  return payload.cancelled || 0;
};

// The per-schema SMS kill switch (SuperAdmin's Messages toggle, or
// sms_enabled) blocks every outbound text, so a queued confirmation fails at
// its send time with "SMS is disabled for schema". One sentence for every
// place that warns about it.
export const smsOffWarning = (schema) =>
  `SMS is turned off for ${schema || "this clinic"} (SuperAdmin → Messages). ` +
  "No confirmation texts can go out while it is off — scheduled confirmations " +
  "will fail when their send time comes.";

// -> { settings, messagingEnabled } or null. `messagingEnabled` is the
// backend's own verdict from the same check that blocks sends; null when the
// server did not say.
export const fetchConfirmationSettings = async ({ schema }) => {
  try {
    const response = await fetch(
      `${getBackendURL()}/twilio/confirmation-settings/?schema=${encodeURIComponent(schema || "public")}`,
    );
    const payload = await response.json();
    if (!response.ok || payload.status !== "success") {
      throw new Error(payload.error || `HTTP ${response.status}`);
    }
    return {
      settings: payload.settings || null,
      messagingEnabled:
        typeof payload.messaging_enabled === "boolean" ? payload.messaging_enabled : null,
    };
  } catch (error) {
    console.warn("[CONFIRMATIONS] Could not load the confirmation settings:", error.message);
    return null;
  }
};

// 'YYYY-MM-DD' shifted by whole days, as a local calendar date (no timezone
// arithmetic, so a DST change never moves the day). null when unparseable.
export const shiftDateString = (dateStr, deltaDays) => {
  const [y, m, d] = String(dateStr || "").split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  dt.setDate(dt.getDate() + deltaDays);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
};

// Apply C/R replies sitting in the SMS history to the appointments in a date
// range (replies that reached the wrong backend and were imported later).
// `dryRun` previews without writing. Throws on failure.
// Every provider schema the Admin page is showing goes in one request: the
// table fans rows in from each selected provider, so checking only the home
// schema found none of the others' appointments ("0 to update, 0 left as is").
export const reconcileConfirmationReplies = async ({ schema, schemas, startDate, endDate, dryRun, userEmail }) => {
  const response = await fetch(`${getBackendURL()}/twilio/reconcile-confirmations/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      schemas: schemas && schemas.length ? schemas : [schema || "public"],
      start_date: startDate,
      end_date: endDate || startDate,
      dry_run: Boolean(dryRun),
      user_email: userEmail || undefined,
    }),
  });
  const payload = await response.json();
  if (!response.ok || payload.status !== "success") {
    throw new Error(payload.error || `HTTP ${response.status}`);
  }
  return payload;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// The reconcile report as ProcessConfirmationsDialog renders it: a summary,
// and the rows sorted under each count — `update` (what changes), `manual`
// (the subset of `update` that staff had set by hand; the patient's text
// overrides it), `kept` (left as they are, with the reason). `done` words the
// warning for a finished run instead of a preview.
export const buildReconcileView = (report, { done = false } = {}) => {
  const applied = report?.applied || [];
  const skipped = report?.skipped || [];
  const counts = report?.counts || {};
  const bySchema = report?.by_schema || {};
  const [start, end] = report?.range || [];
  const fmtAt = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const update = applied.map((a) => {
    const statusChanged = a.status_changed ?? a.from !== a.to;
    return {
      key: `${a.schema || ""}:${a.id}`,
      patient: a.patient_name || a.id,
      clinic: a.schema || null,
      date: a.date_,
      change: statusChanged ? `${a.from || "not asked"} → ${a.to}` : `${a.to} (unchanged)`,
      statusChanged,
      reply: a.reply,
      replyAt: fmtAt(a.reply_at),
      note: a.note || null,
      manual: Boolean(a.manual_to_system),
      setBy: a.manual_to_system ? a.set_by || null : null,
      setAt: a.manual_to_system ? fmtAt(a.set_at) || null : null,
    };
  });
  const manual = update.filter((r) => r.manual);
  const manualChanged = manual.filter((r) => r.statusChanged).length;

  let warning = null;
  if (manual.length) {
    const one = manual.length === 1;
    warning = done
      ? `${plural(manual.length, "appointment")} that ${one ? "was" : "were"} set by hand ${one ? "was" : "were"} switched to the patient's texted answer.`
      : `${plural(manual.length, "appointment")} set by hand will be switched to the patient's texted answer`
        + `${manualChanged ? ` (${manualChanged} with a status change)` : ""}.`;
  }

  return {
    done,
    dateText: start === end ? `on ${start}` : `from ${start} to ${end}`,
    appointments: counts.appointments ?? applied.length + skipped.length,
    clinics: (report?.schemas || []).map((name) => ({
      name, count: bySchema[name] ? bySchema[name].appointments : null,
    })),
    warning,
    errors: report?.errors || [],
    update,
    manual,
    kept: skipped.map((s) => ({
      key: `${s.schema || ""}:${s.id}`,
      patient: s.patient_name || s.id,
      clinic: s.schema || null,
      date: s.date_,
      reason: s.reason,
    })),
    counts: {
      update: update.length,
      manual: manual.length,
      kept: skipped.length,
      statusChanges: counts.status_changes ?? update.filter((r) => r.statusChanged).length,
    },
  };
};
